// Extracted from EnhancedResults.js (structural refactor, July 2026).
// Analysis-result view components — no behavior change.
import React from 'react';

export function PCAView({ data }) {
  if (!data) return null;
  const eigenvalues = data.eigenvalues || {};
  const loadings = data.loadings || {};
  const pcs = Object.keys(eigenvalues);
  const traits = Object.keys(loadings);

  return (
    <div className="space-y-6" data-testid="pca-view">
      {data.interpretation && (
        <div className="bg-[#EAF0EC] rounded-2xl p-5">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      {/* Eigenvalue Summary Table */}
      {pcs.length > 0 && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Eigenvalue Summary</h4>
          <table className="w-full text-sm border-collapse" data-testid="pca-eigenvalue-table">
            <thead>
              <tr className="bg-[#EAF0EC]">
                {['Principal Component', 'Eigenvalue', 'Variance Explained (%)', 'Cumulative Variance (%)'].map(h => (
                  <th key={h} className="border border-[#D5D1CC] px-4 py-2.5 text-center font-semibold text-xs uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pcs.map(pc => {
                const ev = eigenvalues[pc];
                const isImportant = (ev.cumulative_percent || 0) <= 80;
                return (
                  <tr key={pc} className={`hover:bg-[#F5F4F2] ${isImportant ? 'bg-[#EAF0EC]/30' : ''}`}>
                    <td className="border border-[#E8E6E1] px-4 py-2 font-semibold text-center text-[#2C2A28]">{pc}</td>
                    <td className="border border-[#E8E6E1] px-4 py-2 text-center font-mono text-[#5E5A56]">{ev.eigenvalue?.toFixed(4)}</td>
                    <td className="border border-[#E8E6E1] px-4 py-2 text-center font-mono text-[#5E5A56]">
                      <div className="flex items-center gap-2 justify-center">
                        <div className="w-24 bg-[#E8E6E1] rounded-full h-2">
                          <div className="h-2 rounded-full bg-[#6B8E7B]" style={{ width: `${Math.min(ev.variance_percent || 0, 100)}%` }} />
                        </div>
                        <span>{ev.variance_percent?.toFixed(2)}%</span>
                      </div>
                    </td>
                    <td className={`border border-[#E8E6E1] px-4 py-2 text-center font-mono font-bold ${
                      (ev.cumulative_percent || 0) >= 80 ? 'text-emerald-600' : 'text-[#2C2A28]'
                    }`}>
                      {ev.cumulative_percent?.toFixed(2)}%
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="text-xs text-[#8A8580] mt-2">Shaded rows = cumulative variance &le; 80% (important components)</p>
        </div>
      )}

      {/* Component Loadings Table */}
      {traits.length > 0 && pcs.length > 0 && (
        <div className="overflow-x-auto mt-6">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Component Loadings (Eigenvectors)</h4>
          <table className="w-full text-sm border-collapse" data-testid="pca-loadings-table">
            <thead>
              <tr className="bg-[#EAF0EC]">
                <th className="border border-[#D5D1CC] px-4 py-2.5 text-left font-semibold text-xs uppercase sticky left-0 bg-[#EAF0EC] z-10">Trait / Variable</th>
                {pcs.map(pc => (
                  <th key={pc} className="border border-[#D5D1CC] px-4 py-2.5 text-center font-semibold text-xs uppercase">{pc}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {traits.map(trait => (
                <tr key={trait} className="hover:bg-[#F5F4F2]">
                  <td className="border border-[#E8E6E1] px-4 py-2 font-semibold text-[#2C2A28] text-xs sticky left-0 bg-white z-10">{trait}</td>
                  {pcs.map(pc => {
                    const val = loadings[trait]?.[pc];
                    const num = typeof val === 'number' ? val : 0;
                    const absVal = Math.abs(num);
                    return (
                      <td key={pc} className={`border border-[#E8E6E1] px-4 py-2 text-center font-mono text-xs ${
                        absVal > 0.5 ? 'font-bold' : ''
                      } ${absVal > 0.7 ? 'bg-[#EAF0EC] text-[#2C2A28]' : absVal > 0.5 ? 'bg-[#F5F4F2]' : 'text-[#8A8580]'}`}>
                        {num.toFixed(4)}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-xs text-[#8A8580] mt-2">Bold = loading &gt; 0.5 | Highlighted = loading &gt; 0.7 (major contributor to component)</p>
        </div>
      )}

      {data.chart && <img src={`data:image/png;base64,${data.chart}`} alt="PCA Plot" className="w-full rounded-2xl border border-[#E8E6E1] mt-4" data-testid="pca-chart" />}
    </div>
  );
}

/* ============================================================
   HIGH-THROUGHPUT PHENOTYPING VIEW
============================================================ */


export function HTPView({ data }) {
  if (!data) return null;
  const desc = data.descriptive_statistics || {};
  const traits = Object.keys(desc);
  const vegIdx = data.vegetation_indices;
  const herit = data.heritability;
  const outliers = data.outliers;
  const importance = data.trait_importance || {};

  return (
    <div className="space-y-6" data-testid="htp-analysis-view">
      {data.interpretation && (
        <div className="bg-[#E0F2FE] rounded-2xl p-5">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase mb-1">Samples</p>
          <p className="font-mono text-2xl font-bold text-[#2C2A28]">{data.n_samples || '-'}</p>
        </div>
        <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase mb-1">Traits</p>
          <p className="font-mono text-2xl font-bold text-[#0EA5E9]">{data.n_traits || '-'}</p>
        </div>
        <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase mb-1">Veg. Indices</p>
          <p className="font-mono text-2xl font-bold text-[#16a34a]">{vegIdx ? Object.keys(vegIdx).length : 0}</p>
        </div>
        <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase mb-1">Outlier Traits</p>
          <p className="font-mono text-2xl font-bold text-[#E74C3C]">{outliers ? Object.keys(outliers).length : 0}</p>
        </div>
      </div>

      {/* Descriptive Statistics */}
      {traits.length > 0 && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Descriptive Statistics</h4>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#E0F2FE]">
                {['Trait', 'Mean', 'Std', 'CV%', 'Min', 'Max', 'Range', 'Skew', 'Kurt'].map(h => (
                  <th key={h} className="border border-[#D5D1CC] px-3 py-2 text-center font-semibold text-xs uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {traits.map(t => {
                const d = desc[t];
                return (
                  <tr key={t} className="hover:bg-[#F5F4F2]">
                    <td className="border border-[#E8E6E1] px-3 py-2 font-semibold text-xs">{t}</td>
                    {['mean', 'std', 'cv_percent', 'min', 'max', 'range', 'skewness', 'kurtosis'].map(k => (
                      <td key={k} className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs">{d[k]?.toFixed?.(3) ?? d[k] ?? '-'}</td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Heritability */}
      {herit && Object.keys(herit).length > 0 && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Broad-Sense Heritability (H²)</h4>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#EAF0EC]">
                {['Trait', 'H²', 'Vg', 'Ve', 'F-value', 'P-value', 'Category'].map(h => (
                  <th key={h} className="border border-[#D5D1CC] px-3 py-2 text-center font-semibold text-xs uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(herit).map(([t, v]) => (
                <tr key={t} className="hover:bg-[#F5F4F2]">
                  <td className="border border-[#E8E6E1] px-3 py-2 font-semibold text-xs">{t}</td>
                  <td className={`border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs font-bold ${v.H2_broad_sense > 0.6 ? 'text-emerald-600' : v.H2_broad_sense > 0.3 ? 'text-amber-600' : 'text-red-600'}`}>{v.H2_broad_sense}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs">{v.Vg}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs">{v.Ve}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs">{v.F_value}</td>
                  <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs">{v.P_value}</td>
                  <td className={`border border-[#E8E6E1] px-3 py-2 text-center text-xs font-semibold ${v.category === 'High' ? 'text-emerald-600 bg-emerald-50' : v.category === 'Moderate' ? 'text-amber-600 bg-amber-50' : 'text-red-600 bg-red-50'}`}>{v.category}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Trait Importance */}
      {Object.keys(importance).length > 0 && (
        <div>
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Trait Importance (PCA-based)</h4>
          <div className="space-y-2">
            {Object.entries(importance).map(([t, v]) => (
              <div key={t} className="flex items-center gap-3">
                <span className="text-xs font-medium text-[#2C2A28] w-32 truncate">{t}</span>
                <div className="flex-1 bg-[#E8E6E1] rounded-full h-5 relative overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#0EA5E9] to-[#6B8E7B] transition-all" style={{ width: `${Math.min(v * 100, 100)}%` }} />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono font-bold text-[#2C2A28]">{v.toFixed(4)}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.chart && <img src={`data:image/png;base64,${data.chart}`} alt="HTP Charts" className="w-full rounded-2xl border border-[#E8E6E1] mt-4" data-testid="htp-chart" />}
    </div>
  );
}

/* ============================================================
   MOLECULAR BREEDING VIEW
============================================================ */


export function MolecularBreedingView({ data }) {
  if (!data) return null;
  const assoc = data.associations || {};
  const topMarkers = data.top_markers || [];
  const markerStats = data.marker_stats || {};
  const picVals = data.pic_values || {};

  return (
    <div className="space-y-6" data-testid="molecular-breeding-view">
      {data.interpretation && (
        <div className="bg-[#F3E8FF] rounded-2xl p-5">
          <p className="text-sm text-[#2C2A28] leading-relaxed">{data.interpretation}</p>
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase mb-1">Markers</p>
          <p className="font-mono text-2xl font-bold text-[#7C3AED]">{data.n_markers || '-'}</p>
        </div>
        <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase mb-1">Phenotype</p>
          <p className="font-mono text-sm font-bold text-[#2C2A28]">{data.phenotype_trait || '-'}</p>
        </div>
        <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase mb-1">Significant</p>
          <p className="font-mono text-2xl font-bold text-[#E74C3C]">{data.significant_count ?? '-'}</p>
        </div>
        <div className="bg-[#F9F8F6] rounded-2xl p-4 text-center border border-[#E8E6E1]">
          <p className="text-xs text-[#8A8580] uppercase mb-1">Suggestive</p>
          <p className="font-mono text-2xl font-bold text-[#F39C12]">{data.suggestive_count ?? '-'}</p>
        </div>
      </div>

      {/* Top Markers Table */}
      {topMarkers.length > 0 && (
        <div className="overflow-x-auto">
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Top Marker-Trait Associations</h4>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="bg-[#F3E8FF]">
                {['Rank', 'Marker', 'P-value', 'R²', '-log10(P)', 'Effect Size', 'Significance'].map(h => (
                  <th key={h} className="border border-[#D5D1CC] px-3 py-2 text-center font-semibold text-xs uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {topMarkers.map((m, i) => {
                const a = assoc[m.marker] || {};
                return (
                  <tr key={m.marker} className="hover:bg-[#F5F4F2]">
                    <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs font-bold">{i + 1}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 font-semibold text-xs">{m.marker}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs">{m.p_value?.toExponential?.(2) ?? '-'}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs font-bold text-[#7C3AED]">{m.r_squared?.toFixed?.(4) ?? '-'}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs">{a.neg_log10_p?.toFixed?.(2) ?? '-'}</td>
                    <td className="border border-[#E8E6E1] px-3 py-2 text-center font-mono text-xs">{a.effect_size?.toFixed?.(4) ?? '-'}</td>
                    <td className={`border border-[#E8E6E1] px-3 py-2 text-center text-xs font-semibold ${
                      a.highly_significant ? 'text-red-600 bg-red-50' : a.significant ? 'text-amber-600 bg-amber-50' : 'text-[#5E5A56]'
                    }`}>{a.highly_significant ? '**' : a.significant ? '*' : 'NS'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* PIC Values */}
      {Object.keys(picVals).length > 0 && (
        <div>
          <h4 className="font-semibold text-[#2C2A28] mb-3 text-sm uppercase tracking-wider">Polymorphism Information Content (PIC)</h4>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {Object.entries(picVals).slice(0, 12).map(([m, v]) => (
              <div key={m} className="bg-[#F9F8F6] border border-[#E8E6E1] rounded-xl px-3 py-2 flex justify-between items-center">
                <span className="text-xs text-[#5E5A56] truncate">{m}</span>
                <span className={`font-mono text-xs font-bold ${v > 0.5 ? 'text-emerald-600' : v > 0.25 ? 'text-amber-600' : 'text-red-500'}`}>{v.toFixed(3)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {data.chart && <img src={`data:image/png;base64,${data.chart}`} alt="Manhattan Plot" className="w-full rounded-2xl border border-[#E8E6E1] mt-4" data-testid="molecular-chart" />}
    </div>
  );
}


/* ============================================================
   RBD / CRD VIEW
============================================================ */
